import {
  CustomerType,
  DepartmentDataFragment,
  LegalPersonType,
  useAddConsultantWorkExperienceMutation,
  useGetCustomerDepartmentsLazyQuery
} from "@bonliva-crm/graphql";
import locale from "@bonliva-crm/locale";
import {
  CirclePlusIcon,
  Color,
  MonthYearPicker,
  MultiSelect,
  SingleSelect,
  useSnackbarsPipeline
} from "@bonliva-crm/ui";
import { CustomerFullSearchModal } from "@customers-new/customer-search/full/customer-full-search-modal";
import { yupResolver } from "@hookform/resolvers/yup";
import { useGlobalStore } from "@hooks";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputBase,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import { ArrowForward } from "@material-ui/icons";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { WorkExperienceFormValues, workExperienceFormSchema } from "./form-values";

type Props = {
  open: boolean;
  consultantId: string;
  onClose: () => void;
};

export const WorkExperienceModal = (props: Props) => {
  const classes = useStyles();
  const snack = useSnackbarsPipeline();
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [showOtherInformationOpen, setShowOtherInformationOpen] = useState(false);

  const { cache } = useGlobalStore();

  const {
    control,
    watch,
    handleSubmit,
    formState,
    setValue,
    reset: resetForm
  } = useForm<WorkExperienceFormValues>({
    defaultValues: {
      areaOfExpertise: null,
      competitors: [],
      customer: null,
      departments: undefined,
      employmentType: null,
      experience: "",
      from: null,
      roles: "",
      to: null,
      excludeFromPresentation: false
    },
    resolver: yupResolver(workExperienceFormSchema)
  });

  const [
    getCustomerDepartments,
    { data: customerDepartments, loading: customerDepartmentsLoading }
  ] = useGetCustomerDepartmentsLazyQuery({
    fetchPolicy: "network-only"
  });

  const customer = watch("customer");

  useEffect(() => {
    setValue("departments", null, { shouldDirty: true });
    if (customer) {
      getCustomerDepartments({ variables: { customerIds: [customer._id] } });
    }
  }, [customer]);

  useEffect(() => {
    setCustomerSearchOpen(false);
    setShowOtherInformationOpen(false);
  }, [props.open]);

  const [addConsultantWorkExperienceMutation] = useAddConsultantWorkExperienceMutation({
    onCompleted: () => {
      props.onClose();
      resetForm();
      snack.showSuccess("", `${locale.workExperienceAdded}!`);
    },
    onError: (error) => {
      console.error(error);
      snack.showError(locale.ErrorOccurred);
    }
  });

  const addConsultantWorkExperience = (data: WorkExperienceFormValues) => {
    addConsultantWorkExperienceMutation({
      variables: {
        consultantId: props.consultantId,
        data: {
          areaOfExpertise: data.areaOfExpertise,
          employmentType: data.employmentType,
          customerRef: {
            Id: data.customer._id,
            Name: data.customer.Name,
            Type: data.customer.Type
          },
          competitors: data.competitors,
          departmentIds: data.departments?.map((dept) => dept._id) || [],
          experience: data.experience,
          fromMonth: data.from && data.from.getMonth().toString(),
          fromYear: data.from && data.from.getFullYear().toString(),
          toMonth: data.to && data.to.getMonth().toString(),
          toYear: data.to && data.to.getFullYear().toString(),
          roles: data.roles,
          excludeFromPresentation: data.excludeFromPresentation
        }
      }
    });
  };

  const modalContents = () => {
    return (
      <FormControl component="fieldset" className={classes.root}>
        <section className={classes.section}>
          <FormLabel required={true}>
            <Typography variant="body1" component="span">
              {locale["Customer name"]}
            </Typography>
          </FormLabel>
          <Controller
            render={({ field }) => (
              <Box>
                <InputBase
                  onClick={() => setCustomerSearchOpen(true)}
                  fullWidth={true}
                  endAdornment={
                    <ArrowForward
                      htmlColor={Color.Body}
                      style={{ width: "1rem", height: "1rem" }}
                    />
                  }
                  readOnly={true}
                  value={field.value?.Name ?? ""}
                  style={{ padding: "0 1rem" }}
                />
                <CustomerFullSearchModal
                  {...field}
                  open={customerSearchOpen}
                  setOpen={setCustomerSearchOpen}
                  isMulti={false}
                  customerTypes={[CustomerType.Hospital]}
                  instantSelection={true}
                />
              </Box>
            )}
            name="customer"
            control={control}
          />
        </section>

        <section className={classes.section}>
          <FormLabel>
            <Typography variant="body1">{locale.Department}</Typography>
          </FormLabel>
          <Controller
            render={({ field }) => (
              <MultiSelect<{ label: string; value: DepartmentDataFragment }>
                {...field}
                value={
                  field.value
                    ? field.value?.map((dept) => ({ label: dept.Name, value: dept }))
                    : null
                }
                onChange={(depts, actionMeta) => {
                  if (
                    ["deselect-option", "remove-value", "pop-value"].includes(
                      actionMeta.action
                    )
                  ) {
                    field.onChange(
                      depts
                        ? depts
                            .filter((d) => d.value._id !== actionMeta.option?.value._id)
                            .map((dept) => dept.value)
                        : null
                    );
                  } else {
                    field.onChange(depts ? depts.map((dept) => dept.value) : null);
                  }
                }}
                options={
                  customerDepartments?.departments?.items.map((dept) => ({
                    label: dept.Name,
                    value: dept
                  })) || []
                }
                styleProps={{ control: { showBorder: true }, menu: { showBorder: true } }}
                isLoading={customerDepartmentsLoading}
                optionType="checkbox"
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                isDisabled={field.disabled}
                showOptionDivider={false}
                placeholder=""
                isClearable={true}
                isOptionSelected={(option, selectValue) =>
                  selectValue.some((selected) => selected.value._id === option.value._id)
                }
              />
            )}
            name="departments"
            control={control}
            disabled={!watch("customer")}
          />
        </section>
        <section className={classes.section}>
          <FormLabel required={true}>
            <Typography variant="body1" component="span">
              {locale["Employment type"]}
            </Typography>
          </FormLabel>
          <Controller
            render={({ field }) => (
              <RadioGroup {...field}>
                <FormControlLabel
                  value={LegalPersonType.Employee}
                  control={<Radio />}
                  label={locale.cooperationForm.Employee}
                />
                <FormControlLabel
                  value={LegalPersonType.Subcontractor}
                  control={<Radio />}
                  label={locale.cooperationForm.Subcontractor}
                />
              </RadioGroup>
            )}
            name="employmentType"
            control={control}
          />
        </section>
        {watch("employmentType") === LegalPersonType.Subcontractor && (
          <section className={classes.section}>
            <FormLabel required={true}>
              <Typography variant="body1" component="span">
                {locale.ConsultingCompanyLabel}
              </Typography>
            </FormLabel>
            <Controller
              render={({ field }) => (
                <MultiSelect
                  {...field}
                  value={field.value?.map((competitor) => ({
                    label: competitor,
                    value: competitor
                  }))}
                  onChange={(competitors) =>
                    field.onChange(competitors.map((dept) => dept.value))
                  }
                  options={cache.competitors.map((a) => ({
                    label: a,
                    value: a
                  }))}
                  styleProps={{
                    control: { showBorder: true },
                    menu: { showBorder: true }
                  }}
                  optionType="checkbox"
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  showOptionDivider={false}
                  isDisabled={field.disabled}
                  placeholder=""
                />
              )}
              name="competitors"
              control={control}
            />
          </section>
        )}
        <div>
          <Typography variant="h6" style={{ marginBottom: "1rem" }}>{`${
            locale.otherInformation
          } (${locale.notRequired.toLowerCase()})`}</Typography>

          {showOtherInformationOpen ? (
            <Box className={classes.root}>
              <section className={classes.section}>
                <FormLabel>
                  <Typography variant="body1">{locale.AreaOfExpertise}</Typography>
                </FormLabel>
                <Controller
                  render={({ field }) => (
                    <SingleSelect
                      {...field}
                      value={
                        field.value ? { label: field.value, value: field.value } : null
                      }
                      onChange={(value) => field.onChange(value?.value ?? null)}
                      options={cache.areasOfExp.map((a) => ({
                        label: a.Name,
                        value: a.Name
                      }))}
                      styleProps={{
                        control: { showBorder: true },
                        menu: { showBorder: true }
                      }}
                      isClearable={true}
                      placeholder=""
                    />
                  )}
                  name="areaOfExpertise"
                  control={control}
                />
              </section>
              <section className={classes.twoSplit}>
                <div className={classes.fullWidth}>
                  <Controller
                    render={({ field }) => {
                      const { value, onChange, ...rest } = field;
                      return (
                        <MonthYearPicker
                          {...rest}
                          onChange={onChange}
                          date={value}
                          setDate={onChange}
                          label={locale.From}
                        />
                      );
                    }}
                    name="from"
                    control={control}
                  />
                </div>
                <div className={classes.fullWidth}>
                  <Controller
                    render={({ field }) => {
                      const { value, onChange, ...rest } = field;
                      return (
                        <MonthYearPicker
                          {...rest}
                          onChange={onChange}
                          date={value}
                          setDate={onChange}
                          label={locale.To}
                        />
                      );
                    }}
                    name="to"
                    control={control}
                  />
                </div>
              </section>
              <section className={classes.section}>
                <FormLabel>
                  <Typography variant="body1">{locale.Roles}</Typography>
                </FormLabel>
                <Controller
                  render={({ field }) => (
                    <TextField {...field} fullWidth={true}></TextField>
                  )}
                  name="roles"
                  control={control}
                />
              </section>
              <section className={classes.section}>
                <FormLabel>
                  <Typography variant="body1">{locale.Experienced}</Typography>
                </FormLabel>
                <Controller
                  render={({ field }) => (
                    <TextField {...field} fullWidth={true}></TextField>
                  )}
                  name="experience"
                  control={control}
                />
              </section>
              <section className={classes.section}>
                <FormControlLabel
                  labelPlacement="start"
                  control={
                    <Controller
                      render={({ field }) => <Checkbox {...field} />}
                      name="excludeFromPresentation"
                      control={control}
                    />
                  }
                  label={locale.excludeFromPresentation}
                />
              </section>
            </Box>
          ) : (
            <Box
              className={classes.buttonContainer}
              onClick={() => {
                setShowOtherInformationOpen(true);
              }}
            >
              <IconButton style={{ padding: "0" }}>
                <CirclePlusIcon style={{ color: Color.PrimaryMain }} />
              </IconButton>
              <Typography variant="h6" color="primary">
                {locale.otherInformation}
              </Typography>
            </Box>
          )}
        </div>
      </FormControl>
    );
  };

  const canSubmit = formState.isValid && !formState.isSubmitting;

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{locale.workHistory}</DialogTitle>

      <DialogContent>{modalContents()}</DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={props.onClose}>
          {locale.Cancel}
        </Button>

        <Button
          color="primary"
          variant="contained"
          disabled={!canSubmit}
          onClick={handleSubmit((data) => addConsultantWorkExperience(data))}
        >
          {locale.Save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4)
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "0.5rem",
    alignItems: "center",
    "&:hover": {
      cursor: "pointer"
    }
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },
  twoSplit: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    justifyContent: "space-between"
  },
  fullWidth: {
    width: "100%"
  }
}));
